// useYouTubePlayer.js
// ─────────────────────────────────────────────────────────────────────────────
// Responsabilidad única: bootstrap del IFrame API y ciclo de vida del player.
// No sabe nada de música ni de documentales.
// Expone: playerRef, playerReadyRef, ytReadyRef, createPlayer, destroyPlayer
// ─────────────────────────────────────────────────────────────────────────────

import { useRef, useEffect, useCallback } from 'react';

export function useYouTubePlayer() {
    const playerRef = useRef(null);
    const playerReadyRef = useRef(false);
    const ytReadyRef = useRef(false);
    const pendingInitRef = useRef(null);   // init a ejecutar cuando el script YT cargue

    // ── Bootstrap del IFrame API (se ejecuta una sola vez) ───────────────────
    useEffect(() => {
        // Si el script ya estaba cargado antes de montar (ej: hot-reload)
        if (window.YT && window.YT.Player) {
            ytReadyRef.current = true;
        }

        // Encadenamos con cualquier callback previo para no romper otros widgets
        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            ytReadyRef.current = true;
            if (pendingInitRef.current) {
                pendingInitRef.current();
                pendingInitRef.current = null;
            }
            if (typeof prev === 'function') prev();
        };

        if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
            const s = document.createElement('script');
            s.src = 'https://www.youtube.com/iframe_api';
            document.head.appendChild(s);
        }
    }, []);

    // ── createPlayer ─────────────────────────────────────────────────────────
    // containerId : id del <div> donde montar el iframe
    // vid         : YouTube video ID
    // onReady     : callback llamado cuando el player está listo (inicia el poll)
    const createPlayer = useCallback((containerId, vid, onReady) => {
        const init = () => {
            playerRef.current = new window.YT.Player(containerId, {
                // MODO SIN COOKIES (LEGAL.md, L3). El reproductor normal de
                // youtube.com deja sus cookies en cuanto se carga, aunque nadie
                // le dé a play, y la política de cookies de NSL promete que aquí
                // no hay rastreo. Con `host` el iframe se monta contra
                // youtube-nocookie.com y el video se ve igual.
                host: 'https://www.youtube-nocookie.com',
                videoId: vid,
                playerVars: { autoplay: 1, rel: 0, fs: 0, disablekb: 1, modestbranding: 1 },
                events: {
                    onReady: () => {
                        playerReadyRef.current = true;
                        onReady?.();
                    },
                    onError: (e) => console.warn('[NeuraCinema] YT error', e.data),
                },
            });
        };

        if (ytReadyRef.current) {
            init();
        } else {
            // El script todavía está cargando; encolar para onYouTubeIframeAPIReady
            pendingInitRef.current = init;
        }
    }, []);

    // ── destroyPlayer ─────────────────────────────────────────────────────────
    // Solo destruye el player. Detener los polls es responsabilidad del juego.
    const destroyPlayer = useCallback(() => {
        if (playerRef.current) {
            try { playerRef.current.destroy(); } catch (_) { }
            playerRef.current = null;
            playerReadyRef.current = false;
        }
        // Cancelar cualquier init pendiente también
        pendingInitRef.current = null;
    }, []);

    return { playerRef, playerReadyRef, ytReadyRef, createPlayer, destroyPlayer };
}